import type { ApiResponse, Pagination } from '@/shared/types/api';
import type {
  ManagerFiltersType,
  ManagerStatusTypes,
} from '../components/manager/ManagerFilters';
import { api } from '@/shared';
import type { House } from './houseApi';

export const managerApi = {
  getManagers: async (
    params: Partial<ManagerFiltersType>,
  ): Promise<ApiResponse<ManagersResponse>> => {
    try {
      const searchParams = new URLSearchParams();

      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.search) searchParams.append('search', params.search);
      if (params.status) searchParams.append('status', params.status);
      if (params.assignedHouses)
        searchParams.append('assignedHouses', params.assignedHouses.toString());
      if (params.location) searchParams.append('location', params.location);

      const response = await api.get<ApiResponse<ManagersResponse>>(
        `/managers?${searchParams.toString()}`,
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to fetch managers',
      );
    }
  },

  // ✅ New method for fetching manager details by ID
  getManagerById: async (
    id: string,
  ): Promise<ApiResponse<ManagerDetailResponse>> => {
    try {
      const response = await api.get<ApiResponse<ManagerDetailResponse>>(
        `/managers/${id}`,
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to fetch manager details',
      );
    }
  },

  assignHouseToManager: async (
    managerId: string,
    houseIds: string[],
  ): Promise<ApiResponse<null>> => {
    try {
      const response = await api.post<ApiResponse<null>>(
        `/managers/${managerId}/houses/assign`,
        { houseIds },
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to assign house to manager',
      );
    }
  },

  deleteManager: async (managerId: string): Promise<ApiResponse<null>> => {
    try {
      const response = await api.delete<ApiResponse<null>>(
        `/managers/${managerId}`,
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to delete manager',
      );
    }
  },

  inviteManager: async (
    email: string,
    houseId?: string,
  ): Promise<ApiResponse<null>> => {
    try {
      const url = houseId ? `/managers/invite/${houseId}` : '/managers/invite';
      const response = await api.post<ApiResponse<null>>(url, { email });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to invite manager';
      throw new Error(errorMessage);
    }
  },

  // Get invitation details by token (public endpoint)
  getInvitationDetails: async (
    token: string,
  ): Promise<{
    organizationName: string;
    managerName: string;
    status: string;
    house?: { id: string; name: string };
  }> => {
    try {
      const response = await api.get<{
        success: boolean;
        data: {
          organizationName: string;
          managerName: string;
          status: string;
          house?: { id: string; name: string };
        };
      }>(`/managers/invite/details?token=${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to fetch invitation details',
      );
    }
  },

  // Accept invitation (public endpoint)
  acceptInvitation: async (token: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post<ApiResponse<any>>(
        '/managers/invite/accept',
        { token },
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to accept invitation',
      );
    }
  },

  // Reject invitation (public endpoint)
  rejectInvitation: async (token: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post<ApiResponse<any>>(
        '/managers/invite/reject',
        { token },
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to reject invitation',
      );
    }
  },

  /**
   * Get manager permissions
   */
  getManagerPermissions: async (
    managerId: string,
  ): Promise<ManagerPermissions> => {
    try {
      const response = await api.get<
        ApiResponse<{ permissions: ManagerPermissions }>
      >(`/managers/${managerId}/permissions`);
      return response.data.permissions;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to fetch permissions',
      );
    }
  },

  /**
   * Update manager permissions
   */
  updateManagerPermissions: async (
    managerId: string,
    payload: UpdatePermissionsPayload,
  ): Promise<ManagerPermissions> => {
    try {
      const response = await api.patch<
        ApiResponse<{ permissions: ManagerPermissions }>
      >(`/managers/${managerId}/permissions`, payload);
      return response.data.permissions;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to update permissions',
      );
    }
  },
};

export interface ManagerHouse {
  id: string;
  name: string;
  address: string;
  city: string;
}

export interface Manager {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: ManagerStatusTypes;
  assignedHouseCount: number;
  houses: ManagerHouse[];
}

export interface ManagersResponse {
  managers: Manager[];
  pagination: Pagination;
}

export interface ManagerDetail {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  avatar: string | null;
  status: ManagerStatusTypes;
  totalHouses: number;
  totalResidents: number;
  joinedAt: string;
  houses: House[];
  permissions: ManagerPermissions; // Add permissions
  lastActivity: { description: string; createdAt: string; type: string } | null; // Add lastActivity
}

export interface ManagerDetailResponse {
  manager: {
    manager: ManagerDetail;
  };
}

// Response type for assigned house
export interface AssignedHouse {
  id: string;
  houseId: string;
  userId: string;
  assignedAt: string;
}

//  Permission Management Types
export interface ManagerPermissions {
  manageResidents: boolean;
  managePayments: boolean;
  viewReports: boolean;
  postAnnouncements: boolean;
  approveApplications: boolean;
}

export interface UpdatePermissionsPayload {
  permissions: Partial<ManagerPermissions>;
}

// export interface AssignHouseResponse {}
